import { inject, Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormRecord,
  Validators,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { v4 } from 'uuid';

import { MultilanguageFormService } from '@plugins/client/multilanguage/multilanguage-form-service/multilanguage-form.service';
import { GetFormShape } from '@plugins/client/multilanguage/multilanguage-form-service/types';
import { TranslateService } from '@plugins/client/translate/services';
import { BackofficeInstanceDto } from '@plugins/id-card-lib/instance';
import { MultilanguageContent } from '@plugins/shared/types';
import {
  sanitize,
  sanitizeMultilanguageTransformNested,
} from '@plugins/shared/utils/sanitize';

import { DataService } from '../data.service';

import type { BackofficeForm } from './types';
import { validatePermissionKeysUnique } from './validators';

@Injectable()
export class BackofficeFormService extends MultilanguageFormService<
  GetFormShape<BackofficeForm>,
  BackofficeInstanceDto
> {
  protected translateService = inject(TranslateService);
  protected readonly dataService = inject(DataService);
  protected fb = inject(FormBuilder);

  messages = {
    success: this.translateService.instant('backoffice.saveSuccess'),
    invalidMultilanguage: this.translateService.instant(
      'form.formControlError.invalidMultilanguage',
    ),
    failureFallback: 'Unknown error',
  };

  constructor() {
    super();
    this.usePristineIfUnchanged = true;
  }

  get data() {
    return this.dataService.backofficeInstance;
  }

  transformToPatchable(data: BackofficeInstanceDto) {
    return {
      permissions: data.multilanguageData?.['permissions'],
      languages: data.languages,
    };
  }

  protected get multilanguageControls(): FormRecord<FormControl<string>>[] {
    return this.form.controls.permissions.controls.map(
      ({ controls: { description } }) => description,
    );
  }

  saveHandler(): Observable<BackofficeInstanceDto> {
    const { languages, permissions } = this.form.getRawValue();
    const updated = {
      languages,
      multilanguageData: {
        permissions: !permissions
          ? null
          : permissions.map(({ code, description }) => ({
              code: sanitize(code),
              description: sanitizeMultilanguageTransformNested(description),
            })),
      },
    };
    return this.dataService.updateInstance(updated);
  }

  createFormGroup(data: BackofficeInstanceDto) {
    return this.fb.group({
      languages: this.fb.nonNullable.control(data.languages),
      permissions: this.fb.array(
        (data.multilanguageData?.['permissions'] ?? []).map(
          ({ code, description }) =>
            this.createPermissionControl(code, description),
        ),
        { validators: validatePermissionKeysUnique },
      ),
    });
  }

  protected createPermissionControl(
    code?: string,
    description?: MultilanguageContent<string>,
  ) {
    return this.fb.group({
      id: this.fb.control(v4()),
      code: this.fb.control(code, Validators.required),
      description: this.createLanguageControl({
        value: description,
        required: true,
      }),
    });
  }

  addPermission() {
    this.form.controls.permissions.push(this.createPermissionControl());
    this.form.markAsDirty();
  }

  removePermission(index: number) {
    this.form.controls.permissions.removeAt(index);
    this.form.markAsDirty();
  }
}
